import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function getRiskColor(score) {
  if (score >= 75) return '#FF3B30'; // Critical
  if (score >= 60) return '#E65100'; // Elevated  
  if (score >= 40) return '#FF9500'; // Moderate
  return '#2E7D32'; // Low
}

export function getRiskLevel(score) {
  if (score >= 75) return 'CRITICAL';
  if (score >= 60) return 'HIGH';
  if (score >= 40) return 'MEDIUM';
  return 'LOW';
}

export function getRiskBadgeClass(severity) {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL': return 'bg-[#FF3B30] text-white';
    case 'HIGH': return 'bg-[#E65100] text-white';
    case 'MEDIUM': return 'bg-[#FF9500] text-white';
    case 'LOW': return 'bg-[#2E7D32] text-white';
    default: return 'bg-[#4E6974] text-white';
  }
}

export function formatDate(dateString) {
  if (!dateString) return '';
  // Handle GDELT format: 20260330T113000Z
  if (/^\d{8}T\d{6}Z?$/.test(dateString)) {
    const y = dateString.slice(0, 4);
    const m = dateString.slice(4, 6);
    const d = dateString.slice(6, 8);
    const h = dateString.slice(9, 11);
    const min = dateString.slice(11, 13);
    return `${d}/${m}/${y} ${h}:${min}`;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  // Handle GDELT format: 20260330T113000Z
  if (/^\d{8}T\d{6}Z?$/.test(dateString)) {
    const y = dateString.slice(0, 4);
    const m = dateString.slice(4, 6);
    const d = dateString.slice(6, 8);
    const h = dateString.slice(9, 11);
    const min = dateString.slice(11, 13);
    const dt = new Date(`${y}-${m}-${d}T${h}:${min}:00Z`);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    return `${d}/${m}/${y} ${h}:${min}`;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function getTrendIcon(trend) {
  switch (trend?.toLowerCase()) {
    case 'up': return '↑';
    case 'down': return '↓';
    default: return '→';
  }
}

export function getTrendColor(trend) {
  switch (trend?.toLowerCase()) {
    case 'up': return 'text-[#FF3B30]';
    case 'down': return 'text-[#2E7D32]';
    default: return 'text-[#4E6974]';
  }
}

// Risk score interpretation bands (aligned with user spec)
export const RISK_BANDS = [
  { min: 85, max: 100, level: 'Critical', color: '#8B0000', bg: 'bg-[#8B0000]', text: 'text-white', desc: 'Active or imminent disruption. Expect delays, cost increases, or supply interruption. Escalate to leadership and execute business continuity plans immediately.' },
  { min: 70, max: 84, level: 'High', color: '#FF3B30', bg: 'bg-[#FF3B30]', text: 'text-white', desc: 'Significant disruption likely. Immediate review of supplier exposure required. Activate contingency plans and accelerate alternative sourcing.' },
  { min: 50, max: 69, level: 'Elevated', color: '#E65100', bg: 'bg-[#E65100]', text: 'text-white', desc: 'Multiple active risk signals. Disruption is plausible within the next 1-3 months. Actively engage alternative suppliers and review stock levels.' },
  { min: 30, max: 49, level: 'Moderate', color: '#FF9500', bg: 'bg-[#FF9500]', text: 'text-white', desc: 'Some risk signals detected. Monitor developments and review contingency plans. Consider diversifying if heavily concentrated in this area.' },
  { min: 0, max: 29, level: 'Low', color: '#2E7D32', bg: 'bg-[#2E7D32]', text: 'text-white', desc: 'Operating conditions are stable. No significant disruptions expected. Routine monitoring is sufficient.' },
];

export function getRiskBand(score) {
  return RISK_BANDS.find(b => score >= b.min && score <= b.max) || RISK_BANDS[RISK_BANDS.length - 1];
}

export function getRiskInterpretation(score, context = {}) {
  const band = getRiskBand(score);
  const { entityName, entityType, countryName, annualSpend, dominantRisk } = context;
  const s = Math.round(score);

  if (entityType === 'country') {
    const action = s >= 70 ? 'should activate contingency plans for' : s >= 50 ? 'should monitor developments closely and review contingency plans for' : s >= 30 ? 'should continue routine monitoring of' : 'can maintain standard operations with';
    return `${entityName || 'This country'}'s risk score of ${s} is ${band.level.toLowerCase()}. Australian businesses sourcing from ${entityName || 'this country'} ${action} their ${entityName || ''} suppliers.${dominantRisk ? ` Dominant risk factor: ${dominantRisk}.` : ''}`;
  }
  if (entityType === 'supplier') {
    let text = `${entityName || 'This supplier'} has a risk score of ${s} (${band.level.toLowerCase()})`;
    if (dominantRisk) text += ` driven primarily by ${dominantRisk}`;
    text += '.';
    if (annualSpend && annualSpend > 0) {
      const weeklySpend = Math.round(annualSpend / 52);
      const twoWeekImpact = weeklySpend * 2;
      text += ` With your annual spend of $${annualSpend.toLocaleString()}, a 2-week supply disruption could impact approximately $${twoWeekImpact.toLocaleString()} of inbound materials.`;
    }
    return text;
  }
  if (entityType === 'portfolio') {
    return `Your overall supply chain risk is ${s} (${band.level.toLowerCase()}). ${band.desc}`;
  }
  return `Risk score ${s} — ${band.level}. ${band.desc}`;
}

export function getDisruptionEstimate(annualSpend, weeksDisrupted = 2) {
  if (!annualSpend || annualSpend <= 0) return null;
  return Math.round((annualSpend / 52) * weeksDisrupted);
}

export const RISK_CATEGORIES_LABELS = {
  geopolitical: 'Geopolitical instability',
  trade_policy: 'Trade policy & tariffs',
  freight_logistics: 'Freight & logistics',
  regulatory: 'Regulatory & compliance',
  labour_market: 'Labour market',
  climate_disaster: 'Climate & disasters',
  currency_financial: 'Currency & financial',
  supplier_concentration: 'Supplier concentration'
};
